// import { createContext } from "react";

// const AuthContext = createContext({
//   auth: undefined,
//   login: () => null,
//   logout: () => null,
//   setReloadUser: () => null,
// });

// export default AuthContext;

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { useCart } from "./CartContext";
// import { useLogout } from "../hooks/useLogout";

// API
import { setToken, getToken, removeToken } from "../api/token";
import { getMeApi } from "../api/user";

// Utils
import jwtDecode from "jwt-decode";

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

const EXCLUDED_USER_FIELDS = [
  "loginAttempts",
  "updatedAt",
  "_verified",
  "blocked",
];

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({});
  const [user, setUser] = useState({});
  const [totalProductsCart, setTotalProductsCart] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Clean logout function
  const logout = useCallback(() => {
    removeToken();
    setAuth({});
    setUser({});
    setTotalProductsCart(0);
    setError(null);
  }, []);

  // Clean user data filtering
  const filterUserData = useCallback((userData) => {
    console.log('filterUserData input:', userData);

    if (!userData || typeof userData !== 'object') {
      console.warn('filterUserData: userData is null, undefined, or not an object:', userData);
      return { user: {} };
    }

    const { collection, strategy, user, ...rest } = userData;

    if (!user || typeof user !== 'object') {
      console.warn('filterUserData: user is null, undefined, or not an object:', user);
      return { ...rest, user: {} };
    }

    const filteredUser = Object.fromEntries(
      Object.entries(user).filter(
        ([key]) => !EXCLUDED_USER_FIELDS.includes(key)
      )
    );
    return { ...rest, user: filteredUser };
  }, []);

  // Login function with error handling
  const login = useCallback(
    async (token) => {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (decodedToken.exp <= currentTime) {
          throw new Error("Token is expired");
        }

        setToken(token);

        // Fetch full user data from API
        try {
          const response = await getMeApi(token);
          if (response && response.user) {
            const filteredUserData = filterUserData(response);
            setAuth({
              token,
              idUser: decodedToken.id,
              user: filteredUserData.user, // Use API data
            });
            setUser(filteredUserData);
          } else {
            // Fallback to JWT data if API fails
            setAuth({
              token,
              idUser: decodedToken.id,
              user: decodedToken,
            });
          }
        } catch (apiError) {
          console.warn("Failed to fetch user data during login, using JWT data:", apiError);
          // Fallback to JWT data
          setAuth({
            token,
            idUser: decodedToken.id,
            user: decodedToken,
          });
        }

        setError(null);
      } catch (err) {
        console.error("Login failed:", err);
        setError("Login failed - invalid token");
        logout();
      }
    },
    [logout, filterUserData]
  );

  // Validate token without expiration check (for initial load)
  const isValidTokenFormat = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded && decoded.id;
    } catch {
      return false;
    }
  };

  // Check if token is expired
  const isTokenExpired = (decodedToken) => {
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp <= currentTime;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      setError(null);

      const token = getToken();

      if (!token) {
        logout();
        setLoading(false);
        return;
      }

      try {
        // Validate token format first
        if (!isValidTokenFormat(token)) {
          throw new Error("Invalid token format");
        }

        const decodedToken = jwtDecode(token);

        // Check if token is expired
        if (isTokenExpired(decodedToken)) {
          throw new Error("Token expired");
        }

        // Fetch user data first
        const response = await getMeApi(token);
        console.log('getMeApi response:', response);

        if (!response) {
          console.error('getMeApi returned null/undefined');
          throw new Error("Failed to fetch user data");
        }

        // Check if user is null in the response
        if (response.user === null) {
          console.warn('API returned null user, setting empty user object');
          setUser({ user: {} });
          // Set auth state with JWT data as fallback
          setAuth({
            token,
            idUser: decodedToken.id,
            user: decodedToken,
          });
        } else {
          const filteredUserData = filterUserData(response);
          console.log('Filtered user data:', filteredUserData);
          setUser(filteredUserData);

          // Set auth state with full user data from API
          setAuth({
            token,
            idUser: decodedToken.id,
            user: filteredUserData.user, // ✅ Use API user data instead of JWT
          });
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
        setError(error.message);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // Remove token dependency to avoid infinite loops

  // Context value with all necessary functions and state
  const contextValue = {
    auth,
    user,
    login,
    logout,
    setAuth,
    loading,
    error,
    totalProductsCart,
    setTotalProductsCart,
    isAuthenticated: !!auth.token,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}